import {
  AdminInput,
  AdminRadioInput,
  AdminSelect,
  Button,
  createIncidentMessage,
  CreateIncidentMessageDto,
  Disclaimer,
  Div,
  ErrorToast,
  IncidentMessage,
  IncidentMessageType,
  lightTheme,
  P,
  Pill,
  queryKeys,
  reportError,
  SuccessToast,
  updateIncidentMessage,
  vDriveDeliveryStatus,
  vDriveStatuses,
} from '@vgrubs/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ETA_TAG,
  freeStatuses,
  isEtaMessage,
  tagsFor,
  typeOptions,
} from './incidentMessageRules';

type Props = {
  /** Every message on the page, to know which statuses and types are taken. */
  messages: IncidentMessage[];
  /** The message being edited; leave it out to add a new one. */
  incidentMessage?: IncidentMessage;
  closeModal: () => void;
};

export const IncidentMessageModal = ({
  messages,
  incidentMessage,
  closeModal,
}: Props) => {
  const statusOptions = freeStatuses(messages, incidentMessage?.status);
  const etaTaken = messages.some(
    (m) => isEtaMessage(m) && m.id !== incidentMessage?.id
  );

  const [type, setType] = useState<IncidentMessageType>(() => {
    // `?? status` covers an API that predates ETA messages and sends no type.
    if (incidentMessage)
      return incidentMessage.type ?? IncidentMessageType.status;
    return statusOptions.length
      ? IncidentMessageType.status
      : IncidentMessageType.etaIncreased;
  });
  const [status, setStatus] = useState<vDriveDeliveryStatus | undefined>(
    incidentMessage?.status ?? statusOptions[0]
  );
  const [message, setMessage] = useState(incidentMessage?.message ?? '');
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const isEta = type === IncidentMessageType.etaIncreased;

  async function handleSave() {
    if (!message) return ErrorToast('Please enter a message.');
    if (!isEta && !status) return ErrorToast('Please select a status.');
    if (!isEta && message.includes(ETA_TAG))
      return ErrorToast(
        `${ETA_TAG} can only be used in a running late message.`
      );

    // A status message leaves `type` out (the backend defaults it) unless it is switching back
    // from running late, so status messages keep saving against an API without ETA messages.
    const payload: CreateIncidentMessageDto = isEta
      ? { type, message, enabled: incidentMessage?.enabled ?? true }
      : {
          ...(incidentMessage && isEtaMessage(incidentMessage) ? { type } : {}),
          status,
          message,
          enabled: incidentMessage?.enabled ?? true,
        };

    try {
      setLoading(true);
      const { data } = incidentMessage
        ? await updateIncidentMessage(incidentMessage.id, payload)
        : await createIncidentMessage(payload);
      SuccessToast(data.message);
      setLoading(false);
      closeModal();
      queryClient.invalidateQueries({
        queryKey: queryKeys.incidentMessages.all,
      });
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const selectedType = typeOptions().find((option) => option.type === type);

  return (
    <>
      <Div $flex $column $gap="16px">
        <Div $flex $column $gap="8px">
          <P $m="0" $center>
            <b>Type</b>
          </P>
          {typeOptions().map((option) => {
            const disabled =
              option.type === IncidentMessageType.etaIncreased
                ? etaTaken
                : !statusOptions.length;
            return (
              <AdminRadioInput
                key={option.type}
                shadowDesign
                label={
                  disabled ? `${option.label} (already added)` : option.label
                }
                checked={type === option.type}
                disabled={disabled}
                onClick={() => setType(option.type)}
              />
            );
          })}
          <P $m="0" $center $color={lightTheme.lightGray}>
            {selectedType?.description}
          </P>
        </Div>

        {!isEta && (
          <AdminSelect
            label="Status"
            $textCenter
            value={status}
            options={
              <optgroup label="Select Status">
                {statusOptions.map((s) => (
                  <option value={s} key={s}>
                    {vDriveStatuses[s].name}
                  </option>
                ))}
              </optgroup>
            }
            onChange={({ target }: { target: { value: string } }) =>
              setStatus(target.value as vDriveDeliveryStatus)
            }
          />
        )}

        <AdminInput
          label="Message"
          $textCenter
          value={message}
          type="textarea"
          $textareaHeight={170}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter here"
        />
      </Div>
      <Div $flex $wrap $gap="8px" $alignCenter $justifyCenter $p="16px 0">
        {tagsFor(type).map((tag) => (
          <Pill
            key={tag}
            $background={lightTheme.mediumGray}
            $pointer
            onClick={() => setMessage(message + tag)}
          >
            <Disclaimer $m="0" $color={lightTheme.darkGray}>
              {tag}
            </Disclaimer>
          </Pill>
        ))}
      </Div>
      <Div $flex $justifyCenter $p="16px 0" $column $gap="16px">
        <Button $selected $w="100%" onClick={handleSave} $loading={loading}>
          Save
        </Button>
        <Button $selected $w="100%" $gray onClick={closeModal}>
          Cancel
        </Button>
      </Div>
    </>
  );
};
